import * as Yup from "yup";
import { transformSnakeToCamel } from "utils/transform";
import { CategorySchema } from "../products/category.schema";
import { BrandSchema } from "../products/brand.schema";
import { ERROR_MESSAGES } from "constants/validation";

/**
 * Reusable schema for Year/Month filters
 * Used in Transactions, Commissions, and Products Tabs
 */
export const YearMonthSchema = Yup.object().shape({
  year: Yup.string()
    .min(4, ERROR_MESSAGES.MIN_CHARS(4))
    .max(4, ERROR_MESSAGES.MAX_CHARS(4)),
  month: Yup.number()
    .min(1, ERROR_MESSAGES.MIN_VALUE(1))
    .max(12, ERROR_MESSAGES.MAX_VALUE(12))
    .typeError(ERROR_MESSAGES.INVALID_NUMBER),
});

/**
 * Schema for Category/Brand filters
 * Used in Commissions and Products Tabs
 */
export const CategoryBrandFilterSchema = Yup.object().shape({
  filterType: Yup.string().oneOf(["all", "category", "brand"]),
  categoryId: Yup.number().when("filterType", (filterType, schema) => {
    return filterType === "category" ? schema.required(ERROR_MESSAGES.REQUIRED) : schema.nullable();
  }),
  brandId: Yup.number().when("filterType", (filterType, schema) => {
    return filterType === "brand" ? schema.required(ERROR_MESSAGES.REQUIRED) : schema.nullable();
  }),
});

/**
 * Basic filter schema for Tabs that have search and/or date filters
 */
export const SearchFilterSchema = Yup.object().shape({
  searchTerm: Yup.string().nullable(),
  dateRange: Yup.object().shape({
    startDate: Yup.string().nullable(),
    endDate: Yup.string().nullable(),
  }).nullable(),
});

/**
 * Filter Option Schema
 * Represents a filter option item (category or brand)
 * Base schema is loose to allow loading legacy data.
 */
export const FilterOptionSchema = Yup.object().transform(transformSnakeToCamel).shape({
  id: Yup.string().nullable(),
  name: Yup.string().nullable(),
});

/**
 * Filter Validation Schema
 */
export const FilterOptionValidationSchema = FilterOptionSchema.clone().shape({
  id: Yup.string().required(),
  name: Yup.string().required(),
});

/**
 * Filter Options Schema
 * Represents all available filter options for products
 * Base schema is loose to allow loading legacy data.
 */
export const FilterOptionsSchema = Yup.object()
  .transform(transformSnakeToCamel)
  .shape({
    options: Yup.array().of(FilterOptionSchema).nullable(),
    categories: Yup.array().of(CategorySchema).nullable(),
    brands: Yup.array().of(BrandSchema).nullable(),
  });

/**
 * Filter Options Validation Schema
 */
export const FilterOptionsValidationSchema = FilterOptionsSchema.clone().shape({
  options: Yup.array().of(FilterOptionValidationSchema).required(),
  categories: Yup.array().of(CategorySchema).required(),
  brands: Yup.array().of(BrandSchema).required(),
});
